import "./App.css";

function App() {
  return (
    <main>
      {/* HERO */}
      <section className="hero">
        <div className="heroContent">
          <p className="eyebrow">Newton Community Park</p>

          <h1>We support dog parks. We oppose putting one inside Memorial Playground.</h1>

          <p>
            Memorial Playground is one of Oak Hill Park’s only safe, open public spaces for children to play, practice, gather, and be outside. The City of Newton identifies the site as a 3-acre school-age play area with an open lawn, junior baseball field, basketball standards, tennis courts, an intermediate soccer field, and playground amenities, directly abutting Solomon Schechter Day School.
          </p>

          <div className="buttonGroup">
            <a
              href={`mailto:dkalis@newtonma.gov,mwelch@newtonma.gov,mlaredo@newtonma.gov?subject=${encodeURIComponent(
                "Concern About Proposed Dog Park Location"
              )}&body=${encodeURIComponent(
                "I am writing to express concern about the proposed dog park location.\n\nThis is not about whether there is space available. It is about whether this is the right location. The proposed area is directly next to a children’s playground, in front of residential homes, and beside a school.\n\nI respectfully ask that alternative locations be considered.\n\nThank you."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="primaryButton"
            >
              Take Action
            </a>

            <a
              href="https://forms.gle/qK4ZrCXC15fju51YA"
              target="_blank"
              rel="noopener noreferrer"
              className="secondaryButton"
            >
              Add Your Voice
            </a>

          </div>
        </div>
      </section>

      {/* LOCATION ARGUMENT */}
      <section id="learnMore" className="section lightSection">
        <h2>We are neighbors, parents, dog lovers, and dog owners. We believe Newton should have well-designed dog parks in appropriate locations.</h2>
        <p>
          But Memorial Playground is already heavily used by children and youth. It is not unused space. It is a school-adjacent playground, athletic field, recess area, soccer field, basketball area, and community gathering space.
        </p>
        <p>
          The question is not whether Newton should have dog parks.
          The question is whether this particular playground should lose children’s play space to create one.
        </p>
        <p>
          Our answer is: <strong>not here.</strong>
        </p>
      </section>

      {/* CURRENT USE */}
      <section className="section">
        <h2>Why this location is wrong</h2>
        <div className="cards">
          <div className="card">Next to a playground<br/>
            <p>This space is designed for young children. An off leash dog area immediately beside it creates a conflict in use.</p>
          </div>
          <div className="card">In front of homes
            <p>This is a residential area, not a remote park. Activity would take place directly in front of people’s homes.</p>
          </div>
          <div className="card">Beside a school
            <p>The park supports school use. This would change how families and students use the space.</p>
          </div>
          <div className="card">Better locations exist
            <p>Supporting dog parks does not mean every location is appropriate.</p>
          </div>
        </div>
      </section>

      {/* RESIDENTIAL ARGUMENT */}
      <section className="section lightSection">
        <h2>A fully residential setting</h2>
        <p>
          This is not an isolated park or a buffer zone. This space is fully
          surrounded by residential homes and embedded within a neighborhood
          where families live every day.
        </p>
        <p>
          There is no meaningful separation between this space and nearby homes.
          Any change in use would be felt immediately by residents.
        </p>
        <p>
          Introducing an off leash dog area in this exact location would place
          that activity directly into the center of daily residential life.
        </p>
      </section>

      {/* COMMUNITY PERSPECTIVE */}
      <section className="section">
        <h2>Community perspectives</h2>
        <p>
          We recognize that there is interest in creating a dog park in Newton.
        </p>
        <p>
          However, this is not a question of how many people support the idea.
          It is a question of whether this specific location is appropriate.
        </p>
        <p>
          This space is actively used by children, families, and school
          communities. It is not an underutilized area.
        </p>
        <p style={{ fontWeight: "bold" }}>
          This is about choosing the right location for the right use.
        </p>
      </section>

      {/* WHY NOT APPROPRIATE */}
      <section className="section lightSection">
        <h2>Why this location doesn’t work for kids and families</h2>

        <div className="concernList">
          <article>
            <h3>Memorial Playground was built for children and school-age recreation</h3>
            <p>
              Newton’s own park listing describes Memorial Playground as a school-age play area with swings, open lawn, sports fields, basketball standards, tennis courts, and a school parking lot, abutting Solomon Schechter Day School. This is not an empty parcel. It is a children’s recreation space.
            </p>
          </article>

          <article>
            <h3>The field is already actively used</h3>
            <p>
              Newton’s recreation system lists Memorial Playground as a 9v9 soccer practice field, with Valeo Futbol FC scheduled for weekday spring practices from 5:00–8:30 p.m. on multiple dates. Solomon Schechter also runs a Sunday soccer program for young children, ages 15 months through Grade 2, at its Stein Circle campus. This field is already serving kids.
            </p>
          </article>

          <article>
            <h3>Children’s open space is precious</h3>
            <p>
              Safe, walkable play space is hard to replace. Once a fenced dog area is placed inside a small neighborhood playground, that portion of the park is no longer fully available for recess, soccer, scouts, informal play, bikes, tag, family gatherings, or open community use. A fence may make a dog area safer for dogs, but it also permanently divides a shared children’s play space.
            </p>
          </article>

          <article>
            <h3>A school-adjacent dog park raises avoidable safety concerns</h3>
            <p>
              The CDC advises that young children should not play with dogs without supervision and should not approach unfamiliar dogs, even if they seem friendly. That does not mean dogs are bad. It means the safest design is to avoid placing a high-traffic dog area directly inside a high-traffic children’s play area.
            </p>
          </article>
        </div>
      </section>

      {/* GOAL */}
      <section className="section statementSection">
        <h2>Our goal</h2>
        <p>
          Preserve this space for children, families, and community use while
          supporting thoughtful planning for other recreational needs.
        </p>
      </section>

      {/* ACTION */}
      <section className="section actionSection">
        <h2>Take Action</h2>
        <p>
          If this space matters to your family or your neighborhood, your voice
          matters.
        </p>

        <div className="buttonGroup">
          <a
            href="https://forms.gle/qK4ZrCXC15fju51YA"
            target="_blank"
            rel="noopener noreferrer"
            className="primaryButton"
          >
            Add Your Voice
          </a>

          <a
            href={`mailto:dkalis@newtonma.gov,mwelch@newtonma.gov,mlaredo@newtonma.gov?subject=${encodeURIComponent(
                "Concern About Proposed Dog Park Location"
              )}&body=${encodeURIComponent(
                "I am writing to express concern about the proposed dog park location.\n\nThis is not about whether there is space available. It is about whether this is the right location. The proposed area is directly next to a children’s playground, in front of residential homes, and beside a school.\n\nI respectfully ask that alternative locations be considered.\n\nThank you."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="secondaryButton"
          >
            Email City Councilors
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <p>
          Supporting safe, welcoming spaces for children, families, and
          communities in Newton.
        </p>
      </footer>
    </main>
  );
}

export default App;